import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useProjects } from '../context/ProjectContext';
import { useCRM } from '../context/CRMContext';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Checkbox } from '../components/ui/Checkbox';
import { Modal } from '../components/ui/Modal';
import { formatCurrency } from '../utils/calculations';
import { PlusIcon, ChevronDownIcon, ChevronRightIcon, TrashIcon, DollarSignIcon, UserIcon, XIcon, ArrowLeftIcon, StickyNoteIcon, CalendarIcon, ClockIcon } from 'lucide-react';
export function ProjectFlow() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    selectedCompany
  } = useData();
  const {
    getProjectsByCompanyId,
    toggleProjectComplete,
    toggleTaskComplete,
    deleteProject,
    deleteTask,
    addProject,
    addTask,
    updateProject,
    addNote,
    deleteNote
  } = useProjects();
  const {
    getContactsByCompanyId
  } = useCRM();
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [newProjectTitle, setNewProjectTitle] = useState('');
  const [newProjectValue, setNewProjectValue] = useState('');
  const [newProjectContactId, setNewProjectContactId] = useState('');
  const [newTaskTitles, setNewTaskTitles] = useState<Record<string, string>>({});
  const [newTaskValues, setNewTaskValues] = useState<Record<string, string>>({});
  const [contactFilter, setContactFilter] = useState('');
  // Note modal state
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [noteDateTime, setNoteDateTime] = useState('');
  const [includeDateTime, setIncludeDateTime] = useState(false);
  const projects = selectedCompany ? getProjectsByCompanyId(selectedCompany.id) : [];
  const contacts = selectedCompany ? getContactsByCompanyId(selectedCompany.id) : [];
  // Check for contact filter in URL params
  useEffect(() => {
    const contactId = searchParams.get('contact');
    if (contactId) {
      setContactFilter(contactId);
    }
  }, [searchParams]);
  // Filter projects by contact if filter is active
  const filteredProjects = contactFilter ? projects.filter(p => p.contact_id === contactFilter) : projects;
  const clearContactFilter = () => {
    setContactFilter('');
    setSearchParams({});
  };
  const goBackToContact = () => {
    if (contactFilter) {
      navigate(`/lead-flow/view/${contactFilter}`);
    }
  };
  const toggleExpanded = (projectId: string) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };
  const handleAddProject = () => {
    if (!newProjectTitle.trim() || !selectedCompany) return;
    addProject({
      company_id: selectedCompany.id,
      title: newProjectTitle,
      completed: false,
      value: parseFloat(newProjectValue) || 0,
      contact_id: newProjectContactId || undefined,
      tasks: [],
      notes: []
    });
    setNewProjectTitle('');
    setNewProjectValue('');
    setNewProjectContactId('');
  };
  const handleAddTask = (projectId: string) => {
    const title = newTaskTitles[projectId];
    const value = newTaskValues[projectId];
    if (!title?.trim()) return;
    addTask(projectId, {
      title,
      completed: false,
      value: parseFloat(value) || 0
    });
    setNewTaskTitles({
      ...newTaskTitles,
      [projectId]: ''
    });
    setNewTaskValues({
      ...newTaskValues,
      [projectId]: ''
    });
  };
  const handleContactChange = (projectId: string, contactId: string) => {
    updateProject(projectId, {
      contact_id: contactId || undefined
    });
  };
  const handleOpenNoteModal = (projectId: string) => {
    setActiveProjectId(projectId);
    setNoteText('');
    setNoteDateTime('');
    setIncludeDateTime(false);
    setShowNoteModal(true);
  };
  const handleSaveNote = () => {
    if (!activeProjectId || !noteText.trim()) return;
    addNote(activeProjectId, {
      text: noteText.trim().substring(0, 50),
      date_time: includeDateTime && noteDateTime ? new Date(noteDateTime) : undefined
    });
    setShowNoteModal(false);
    setActiveProjectId(null);
    setNoteText('');
    setNoteDateTime('');
    setIncludeDateTime(false);
  };
  const handleDeleteNote = (projectId: string, noteId: string) => {
    deleteNote(projectId, noteId);
  };
  const getTotalValue = (projectsList = projects) => {
    return projectsList.reduce((sum, project) => sum + project.value, 0);
  };
  const getCompletedValue = (projectsList = projects) => {
    return projectsList.filter(p => p.completed).reduce((sum, project) => sum + project.value, 0);
  };
  const getTaskProgress = (project: (typeof projects)[0]) => {
    if (project.tasks.length === 0) return 0;
    const completed = project.tasks.filter(t => t.completed).length;
    return Math.round(completed / project.tasks.length * 100);
  };
  const getContactName = (contactId?: string) => {
    if (!contactId) return null;
    const contact = contacts.find(c => c.id === contactId);
    return contact ? `${contact.first_name} ${contact.last_name}` : null;
  };
  const filteredContact = contactFilter ? contacts.find(c => c.id === contactFilter) : null;
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-1681">
      <div className="flex items-center justify-between" data-id="element-1682">
        <div data-id="element-1683">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1684">Projects</h1>
          <p className="text-sm text-gray-600" data-id="element-1685">
            {contactFilter ? <>
                {filteredProjects.length} of {projects.length} projects •{' '}
                {formatCurrency(getTotalValue(filteredProjects))} value
              </> : <>
                {projects.length} projects • {formatCurrency(getTotalValue())}{' '}
                total value
              </>}
          </p>
        </div>
        <div className="text-right" data-id="element-1686">
          <p className="text-sm text-gray-600" data-id="element-1687">Completed</p>
          <p className="text-lg font-bold text-green-600" data-id="element-1688">
            {formatCurrency(getCompletedValue(filteredProjects))}
          </p>
        </div>
      </div>

      {/* Contact Filter Banner */}
      {contactFilter && filteredContact && <Card data-id="element-1689">
          <div className="flex items-center justify-between" data-id="element-1690">
            <div className="flex items-center gap-3" data-id="element-1691">
              <UserIcon className="w-5 h-5 text-orange-600" data-id="element-1692" />
              <div data-id="element-1693">
                <p className="text-sm font-medium text-gray-900" data-id="element-1694">
                  Showing projects for: {filteredContact.first_name}{' '}
                  {filteredContact.last_name}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1695">
                  {filteredContact.company}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-1696">
              <Button variant="secondary" size="sm" onClick={goBackToContact} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-1698" />} data-id="element-1697">
                Back to Contact
              </Button>
              <Button variant="ghost" size="sm" onClick={clearContactFilter} leftIcon={<XIcon className="w-4 h-4" data-id="element-1700" />} data-id="element-1699">
                Clear Filter
              </Button>
            </div>
          </div>
        </Card>}

      {/* Note Modal */}
      <Modal isOpen={showNoteModal} onClose={() => setShowNoteModal(false)} title="Add Note" data-id="element-1701">
        <div className="space-y-4" data-id="element-1702">
          <div data-id="element-1703">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1704">
              Note Text{' '}
              <span className="text-gray-500" data-id="element-1705">(max 50 characters)</span>
            </label>
            <input type="text" maxLength={50} placeholder="e.g., Call client tomorrow, Waiting on approval..." value={noteText} onChange={e => setNoteText(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-1706" />
            <p className="text-xs text-gray-500 mt-1" data-id="element-1707">
              {noteText.length}/50 characters
            </p>
          </div>

          <div className="flex items-center gap-2" data-id="element-1708">
            <Checkbox checked={includeDateTime} onChange={checked => setIncludeDateTime(checked)} data-id="element-1709" />
            <label className="text-sm font-medium text-gray-700" data-id="element-1710">
              Include date & time
            </label>
          </div>

          {includeDateTime && <div data-id="element-1711">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1712">
                Date & Time
              </label>
              <input type="datetime-local" value={noteDateTime} onChange={e => setNoteDateTime(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-1713" />
            </div>}

          <div className="flex justify-end gap-2 pt-2" data-id="element-1714">
            <Button variant="ghost" onClick={() => setShowNoteModal(false)} data-id="element-1715">
              Cancel
            </Button>
            <Button onClick={handleSaveNote} disabled={!noteText.trim()} data-id="element-1716">
              Add Note
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add New Project */}
      <Card data-id="element-1717">
        <div className="space-y-3" data-id="element-1718">
          <div className="flex gap-3" data-id="element-1719">
            <input type="text" placeholder="New project title..." value={newProjectTitle} onChange={e => setNewProjectTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddProject()} className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-1720" />
            <div className="relative w-32" data-id="element-1721">
              <DollarSignIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1722" />
              <input type="number" placeholder="Value" value={newProjectValue} onChange={e => setNewProjectValue(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddProject()} className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-1723" />
            </div>
          </div>
          <div className="flex gap-3" data-id="element-1724">
            <select value={newProjectContactId} onChange={e => setNewProjectContactId(e.target.value)} className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-1725">
              <option value="" data-id="element-1726">Select Customer (Optional)</option>
              {contacts.map(contact => <option key={contact.id} value={contact.id} data-id="element-1727">
                  {contact.first_name} {contact.last_name} - {contact.company}
                </option>)}
            </select>
            <Button onClick={handleAddProject} size="sm" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1729" />} data-id="element-1728">
              Add
            </Button>
          </div>
        </div>
      </Card>

      {/* Projects List */}
      <div className="space-y-3" data-id="element-1730">
        {filteredProjects.map(project => {
        const isExpanded = expandedProjects.has(project.id);
        const progress = getTaskProgress(project);
        const contactName = getContactName(project.contact_id);
        const notes = project.notes || [];
        return <Card key={project.id} padding="none" data-id="element-1731">
              {/* Project Header */}
              <div className="p-4" data-id="element-1732">
                <div className="flex items-start gap-3" data-id="element-1733">
                  <Checkbox checked={project.completed} onChange={() => toggleProjectComplete(project.id)} className="mt-0.5" data-id="element-1734" />
                  <button onClick={() => toggleExpanded(project.id)} className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-1735">
                    {isExpanded ? <ChevronDownIcon className="w-4 h-4 text-gray-600" data-id="element-1736" /> : <ChevronRightIcon className="w-4 h-4 text-gray-600" data-id="element-1737" />}
                  </button>
                  <div className="flex-1 min-w-0" data-id="element-1738">
                    <div className="flex items-center justify-between gap-3 mb-2" data-id="element-1739">
                      <div className="flex-1 min-w-0" data-id="element-1740">
                        <h3 className={`text-base font-semibold ${project.completed ? 'text-gray-400 line-through' : 'text-gray-900'}`} data-id="element-1741">
                          {project.title}
                        </h3>
                        {contactName && <div className="flex items-center gap-1 mt-1" data-id="element-1742">
                            <UserIcon className="w-3 h-3 text-gray-400" data-id="element-1743" />
                            <span className="text-xs text-gray-500" data-id="element-1744">
                              {contactName}
                            </span>
                          </div>}
                      </div>
                      <div className="flex items-center gap-3" data-id="element-1745">
                        <span className="text-sm font-semibold text-gray-900 whitespace-nowrap" data-id="element-1746">
                          {formatCurrency(project.value)}
                        </span>
                        <button onClick={() => handleOpenNoteModal(project.id)} className="p-1 text-gray-400 hover:text-yellow-600 transition-colors" title="Add note" data-id="element-1747">
                          <StickyNoteIcon className="w-4 h-4" data-id="element-1748" />
                        </button>
                        <button onClick={() => deleteProject(project.id)} className="p-1 text-gray-400 hover:text-red-600 transition-colors" data-id="element-1749">
                          <TrashIcon className="w-4 h-4" data-id="element-1750" />
                        </button>
                      </div>
                    </div>

                    {/* Notes Display */}
                    {notes.length > 0 && <div className="flex flex-wrap gap-2 mb-2" data-id="element-1751">
                        {notes.map(note => <div key={note.id} className="group relative inline-flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-2 py-1 text-xs" data-id="element-1752">
                            <StickyNoteIcon className="w-3 h-3 text-yellow-600 flex-shrink-0" data-id="element-1753" />
                            <span className="text-yellow-900 font-medium" data-id="element-1754">
                              {note.text}
                            </span>
                            {note.date_time && <div className="flex items-center gap-1 text-yellow-700" data-id="element-1755">
                                <ClockIcon className="w-3 h-3" data-id="element-1756" />
                                <span data-id="element-1757">
                                  {new Date(note.date_time).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                                </span>
                              </div>}
                            <button onClick={() => handleDeleteNote(project.id, note.id)} className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 text-yellow-600 hover:text-red-600" data-id="element-1758">
                              <XIcon className="w-3 h-3" data-id="element-1759" />
                            </button>
                          </div>)}
                      </div>}

                    {project.tasks.length > 0 && <div className="flex items-center gap-3" data-id="element-1760">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-1761">
                          <div className="h-full bg-blue-500 transition-all duration-300" style={{
                      width: `${progress}%`
                    }} data-id="element-1762" />
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap" data-id="element-1763">
                          {project.tasks.filter(t => t.completed).length}/
                          {project.tasks.length} tasks
                        </span>
                      </div>}
                  </div>
                </div>
              </div>

              {/* Tasks (when expanded) */}
              {isExpanded && <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-3" data-id="element-1764">
                  {/* Customer Selection */}
                  <div className="pl-8" data-id="element-1765">
                    <label className="text-xs font-medium text-gray-600 mb-1 block" data-id="element-1766">
                      Customer
                    </label>
                    <select value={project.contact_id || ''} onChange={e => handleContactChange(project.id, e.target.value)} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-1767">
                      <option value="" data-id="element-1768">No customer assigned</option>
                      {contacts.map(contact => <option key={contact.id} value={contact.id} data-id="element-1769">
                          {contact.first_name} {contact.last_name} -{' '}
                          {contact.company}
                        </option>)}
                    </select>
                  </div>

                  {/* Tasks */}
                  {project.tasks.map(task => <div key={task.id} className="flex items-center gap-3 pl-8" data-id="element-1770">
                      <Checkbox checked={task.completed} onChange={() => toggleTaskComplete(project.id, task.id)} data-id="element-1771" />
                      <span className={`flex-1 text-sm ${task.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`} data-id="element-1772">
                        {task.title}
                      </span>
                      <span className="text-sm font-medium text-gray-600" data-id="element-1773">
                        {formatCurrency(task.value)}
                      </span>
                      <button onClick={() => deleteTask(project.id, task.id)} className="p-1 text-gray-400 hover:text-red-600 transition-colors" data-id="element-1774">
                        <TrashIcon className="w-3 h-3" data-id="element-1775" />
                      </button>
                    </div>)}

                  {/* Add Task */}
                  <div className="flex gap-2 pl-8 pt-2" data-id="element-1776">
                    <input type="text" placeholder="Add task..." value={newTaskTitles[project.id] || ''} onChange={e => setNewTaskTitles({
                ...newTaskTitles,
                [project.id]: e.target.value
              })} onKeyDown={e => e.key === 'Enter' && handleAddTask(project.id)} className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-1777" />
                    <div className="relative w-24" data-id="element-1778">
                      <DollarSignIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" data-id="element-1779" />
                      <input type="number" placeholder="Value" value={newTaskValues[project.id] || ''} onChange={e => setNewTaskValues({
                  ...newTaskValues,
                  [project.id]: e.target.value
                })} onKeyDown={e => e.key === 'Enter' && handleAddTask(project.id)} className="w-full pl-6 pr-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-1780" />
                    </div>
                    <Button onClick={() => handleAddTask(project.id)} size="sm" variant="secondary" data-id="element-1781">
                      Add
                    </Button>
                  </div>
                </div>}
            </Card>;
      })}
      </div>

      {filteredProjects.length === 0 && <Card data-id="element-1782">
          <div className="text-center py-8" data-id="element-1783">
            <p className="text-gray-500 mb-4" data-id="element-1784">
              {contactFilter ? `No projects found for ${filteredContact?.first_name} ${filteredContact?.last_name}` : 'No projects yet. Add your first project above!'}
            </p>
            {contactFilter && <div className="flex items-center justify-center gap-3" data-id="element-1785">
                <Button onClick={goBackToContact} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-1787" />} data-id="element-1786">
                  Back to Contact
                </Button>
                <Button onClick={clearContactFilter} variant="secondary" data-id="element-1788">
                  View All Projects
                </Button>
              </div>}
          </div>
        </Card>}
    </div>;
}